import React from 'react'
import type { NotificationFacetList, NotificationFacets } from '../../types/notifications'

export interface InboxFilterState {
  unreadOnly: boolean
  groups: string[]
  listId: number | null
}

interface InboxFiltersProps {
  value: InboxFilterState
  facets: NotificationFacets | null
  unreadCount: number
  onChange: (next: InboxFilterState) => void
}

const PILL =
  'inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-[13px] font-semibold transition-colors'

function pillClass(active: boolean): string {
  return active
    ? `${PILL} border-ink bg-ink text-paper`
    : `${PILL} border-rule-strong bg-paper text-ink hover:border-ink-300`
}

/** Nesting level of a list in the facet list (which comes in tree order). */
function listDepth(lists: NotificationFacetList[], list: NotificationFacetList): number {
  let depth = 0
  let parentId = list.parentId ?? null
  while (parentId !== null && depth < 5) {
    const parent = lists.find((l) => l.id === parentId)
    if (!parent) break
    depth++
    parentId = parent.parentId ?? null
  }
  return depth
}

/**
 * Inbox filters (DESIGN.md → filter pills): all/unread, change type and
 * watchlist list. Filtering happens on the server, so counts and paging
 * stay right.
 */
const InboxFilters: React.FC<InboxFiltersProps> = ({ value, facets, unreadCount, onChange }) => {
  const toggleGroup = (id: string) => {
    const groups = value.groups.includes(id)
      ? value.groups.filter((g) => g !== id)
      : [...value.groups, id]
    onChange({ ...value, groups })
  }
  const groups = (facets?.groups ?? []).filter((g) => g.count > 0 || value.groups.includes(g.id))
  const lists = facets?.lists ?? []

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="inbox-filters">
      <div className="inline-flex rounded-full border border-rule-strong bg-paper p-0.5" role="group" aria-label="Vis">
        <button
          type="button"
          aria-pressed={!value.unreadOnly}
          onClick={() => onChange({ ...value, unreadOnly: false })}
          className={`h-7 rounded-full px-3 text-[13px] font-semibold ${!value.unreadOnly ? 'bg-ink text-paper' : 'text-ink-600 hover:text-ink'}`}
        >
          Alle
        </button>
        <button
          type="button"
          aria-pressed={value.unreadOnly}
          onClick={() => onChange({ ...value, unreadOnly: true })}
          className={`h-7 rounded-full px-3 text-[13px] font-semibold ${value.unreadOnly ? 'bg-ink text-paper' : 'text-ink-600 hover:text-ink'}`}
        >
          Ulæste{unreadCount > 0 ? ` ${unreadCount}` : ''}
        </button>
      </div>

      {groups.length > 0 && <span className="mx-1 h-5 w-px bg-rule" aria-hidden />}

      {groups.map((g) => {
        const active = value.groups.includes(g.id)
        return (
          <button
            key={g.id}
            type="button"
            aria-pressed={active}
            onClick={() => toggleGroup(g.id)}
            className={pillClass(active)}
          >
            {g.label}
            <span className={`tabular-nums ${active ? 'text-paper/70' : 'text-ink-500'}`}>{g.count}</span>
          </button>
        )
      })}

      {lists.length > 0 && (
        <label className="ml-auto inline-flex items-center gap-2 text-[13px] font-semibold text-ink-500">
          Liste
          <select
            value={value.listId ?? ''}
            onChange={(e) =>
              onChange({ ...value, listId: e.target.value === '' ? null : Number(e.target.value) })
            }
            className="h-8 rounded-md border border-rule-strong bg-paper px-2 text-[13px] font-semibold text-ink"
          >
            <option value="">Alle lister</option>
            {lists.map((l) => (
              <option key={l.id} value={l.id}>
                {'\u2003'.repeat(listDepth(lists, l))}
                {l.name} ({l.count})
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  )
}

export default InboxFilters
